import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { ChannelLogo } from '../components/channel-logo';
import { CheckIcon } from '../components/icons';
import { COUNTRIES, type CountryOption } from '../config/countries';
import { FALLBACK_CHANNELS } from '../config/reference-data';
import { useChannelsQuery, useToggleChannelSubscribe } from '../hooks/use-channels-query';
import { useCreateProfileMutation } from '../hooks/use-profile-query';
import {
  useActivePlatformLanguageOptions,
  useKnownLanguageOptions,
  type ActivePlatformLanguageView,
} from '../hooks/use-platform-languages-query';
import { useAuth } from '../hooks/use-auth';
import { rankChannelsByCountry } from '../lib/channel-suggest';

const TOTAL_STEPS = 4;

export function Onboarding() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [homeCountry, setHomeCountry] = useState('');
  const [currentCountry, setCurrentCountry] = useState('');
  const [city, setCity] = useState('');
  const [primary, setPrimary] = useState('en');
  const [known, setKnown] = useState<readonly string[]>(['en']);
  const [channels, setChannels] = useState<ReadonlySet<string>>(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch country-filtered channels alongside the general list so the
  // ranking has real candidates from the user's countries to surface,
  // even when the alphabetical /channels page doesn't include them.
  const generalChannelsQuery = useChannelsQuery({ limit: 24 });
  const currentCountryChannelsQuery = useChannelsQuery({
    country: currentCountry || null,
    limit: 12,
    enabled: !!currentCountry,
  });
  const homeCountryChannelsQuery = useChannelsQuery({
    country: homeCountry && homeCountry !== currentCountry ? homeCountry : null,
    limit: 12,
    enabled: !!homeCountry && homeCountry !== currentCountry,
  });
  const subscribe = useToggleChannelSubscribe();
  const createProfile = useCreateProfileMutation();
  const languageOptions = useActivePlatformLanguageOptions();
  const knownPickerLanguages = useKnownLanguageOptions();
  const knownLanguageOptions =
    knownPickerLanguages.length > 0 ? knownPickerLanguages : languageOptions;

  // Country-first sort, then slice to 12 — mirrors mobile's
  // priority sort in follow-channels-screen.tsx.
  const channelOptions = useMemo(() => {
    const seen = new Set<string>();
    const merged: NonNullable<typeof generalChannelsQuery.data> = [];
    for (const list of [
      currentCountryChannelsQuery.data,
      homeCountryChannelsQuery.data,
      generalChannelsQuery.data,
    ]) {
      if (!list) continue;
      for (const c of list) {
        if (seen.has(c.id)) continue;
        seen.add(c.id);
        merged.push(c);
      }
    }
    if (merged.length > 0) {
      const ranked = rankChannelsByCountry(
        merged,
        currentCountry || null,
        homeCountry || null,
      );
      return ranked.slice(0, 12).map((c) => ({
        id: c.id,
        name: c.name,
        lang: c.language?.toUpperCase() ?? '',
        country: c.country ?? '',
      }));
    }
    return FALLBACK_CHANNELS.map((c) => ({ ...c }));
  }, [
    currentCountryChannelsQuery.data,
    homeCountryChannelsQuery.data,
    generalChannelsQuery.data,
    currentCountry,
    homeCountry,
  ]);

  const finish = async () => {
    if (!user) return;
    setError(null);
    setSubmitting(true);
    try {
      await createProfile.mutateAsync({
        primaryLanguage: primary,
        knownLanguages: Array.from(new Set(known)),
        homeCountry: homeCountry || undefined,
        currentCountry: currentCountry || homeCountry || undefined,
        city: city.trim() || undefined,
        displayName: (user.user_metadata?.display_name as string | undefined) ?? undefined,
      });
      await Promise.allSettled(
        Array.from(channels).map((id) => subscribe.mutateAsync({ channelId: id, subscribe: true })),
      );
      navigate('/home', { replace: true });
    } catch (err) {
      setError((err as Error).message ?? t('web.onboarding.errors.saveFailed'));
      setSubmitting(false);
    }
  };

  const canAdvance = (): boolean => {
    if (step === 1) return homeCountry.length > 0;
    return true;
  };

  const next = () => {
    if (step < TOTAL_STEPS) setStep((s) => s + 1);
    else void finish();
  };
  const back = () => step > 1 && setStep((s) => s - 1);

  return (
    <div className="nw-onboard">
      <ProgressBar step={step} total={TOTAL_STEPS} />
      {step === 1 && (
        <StepLocation
          t={t}
          homeCountry={homeCountry}
          currentCountry={currentCountry}
          city={city}
          onHomeChange={(v) => {
            setHomeCountry(v);
            // Default current to home so a non-expat doesn't have to pick twice.
            if (!currentCountry) setCurrentCountry(v);
          }}
          onCurrentChange={setCurrentCountry}
          onCityChange={setCity}
          countries={COUNTRIES}
        />
      )}
      {step === 2 && (
        <StepLanguage t={t} value={primary} onChange={setPrimary} languages={languageOptions} />
      )}
      {step === 3 && (
        <StepKnown
          t={t}
          value={known}
          onChange={(nextKnown) => {
            setKnown(nextKnown.slice(0, 5));
          }}
          primary={primary}
          languages={knownLanguageOptions}
        />
      )}
      {step === 4 && <StepChannels t={t} value={channels} onChange={setChannels} options={channelOptions} />}
      {error && (
        <div className="nw-modal__error" role="alert">
          {error}
        </div>
      )}
      <div className="nw-onboard__footer">
        {step > 1 ? (
          <button type="button" className="nw-btn nw-btn--ghost" onClick={back} disabled={submitting}>
            {t('common.actions.back')}
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          className="nw-btn nw-btn--primary nw-btn--lg"
          onClick={next}
          disabled={submitting || !canAdvance()}
        >
          {step < TOTAL_STEPS
            ? t('common.actions.continue')
            : submitting
            ? t('common.actions.saving')
            : t('common.actions.startReading')}
        </button>
      </div>
    </div>
  );
}

function ProgressBar({ step, total }: { readonly step: number; readonly total: number }) {
  const { t } = useTranslation();
  return (
    <div
      className="nw-onboard__progress"
      aria-label={t('web.onboarding.progress.stepAria', { step, total })}
    >
      {Array.from({ length: total }).map((_, i) => (
        <span key={i} className={i < step ? 'is-on' : ''} />
      ))}
    </div>
  );
}

function StepLocation({
  t,
  homeCountry,
  currentCountry,
  city,
  onHomeChange,
  onCurrentChange,
  onCityChange,
  countries,
}: {
  readonly t: TFunction;
  readonly homeCountry: string;
  readonly currentCountry: string;
  readonly city: string;
  readonly onHomeChange: (v: string) => void;
  readonly onCurrentChange: (v: string) => void;
  readonly onCityChange: (v: string) => void;
  readonly countries: readonly CountryOption[];
}) {
  return (
    <>
      <div className="nw-onboard__step-label">{t('web.onboarding.step1.label')}</div>
      <h1 className="nw-onboard__title">{t('web.onboarding.step1.title')}</h1>
      <p className="nw-onboard__sub">{t('web.onboarding.step1.subtitle')}</p>
      <div className="nw-form-grid">
        <label className="nw-field">
          <span className="nw-field__label">{t('web.onboarding.step1.homeLabel')}</span>
          <CountrySelect
            value={homeCountry}
            onChange={onHomeChange}
            countries={countries}
            placeholder="—"
          />
        </label>
        <label className="nw-field">
          <span className="nw-field__label">{t('web.onboarding.step1.currentLabel')}</span>
          <CountrySelect
            value={currentCountry}
            onChange={onCurrentChange}
            countries={countries}
            placeholder="—"
          />
        </label>
        <label className="nw-field">
          <span className="nw-field__label">{t('web.onboarding.step1.cityLabel')}</span>
          <input
            type="text"
            className="nw-input"
            value={city}
            onChange={(e) => onCityChange(e.target.value)}
            placeholder={t('web.onboarding.step1.cityPlaceholder') ?? ''}
            autoComplete="address-level2"
          />
        </label>
      </div>
    </>
  );
}

function CountrySelect({
  value,
  onChange,
  countries,
  placeholder,
}: {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly countries: readonly CountryOption[];
  readonly placeholder: string;
}) {
  return (
    <select
      className="nw-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={placeholder}
    >
      <option value="">{placeholder}</option>
      {countries.map((c) => (
        <option key={c.code} value={c.code}>
          {c.flag} {c.name}
        </option>
      ))}
    </select>
  );
}

function StepLanguage({
  t,
  value,
  onChange,
  languages,
}: {
  readonly t: TFunction;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly languages: readonly ActivePlatformLanguageView[];
}) {
  const visible = languages.slice(0, 8);
  return (
    <>
      <div className="nw-onboard__step-label">{t('web.onboarding.step2.label')}</div>
      <h1 className="nw-onboard__title">{t('web.onboarding.step2.title')}</h1>
      <p className="nw-onboard__sub">{t('web.onboarding.step2.subtitle')}</p>
      <div className="nw-radio-group">
        {visible.map((l) => (
          <button
            type="button"
            key={l.code}
            className={`nw-radio-row ${value === l.code ? 'is-on' : ''}`}
            onClick={() => onChange(l.code)}
            aria-pressed={value === l.code}
          >
            <span className="nw-radio-row__flag">{l.flag}</span>
            <span className="nw-radio-row__name">{l.name}</span>
            <span className="nw-radio-row__native">{l.native}</span>
            <span className="nw-radio-row__check">{value === l.code && <CheckIcon size={12} stroke={3} />}</span>
          </button>
        ))}
      </div>
    </>
  );
}

function StepKnown({
  t,
  value,
  onChange,
  primary,
  languages,
}: {
  readonly t: TFunction;
  readonly value: readonly string[];
  readonly onChange: (v: readonly string[]) => void;
  readonly primary: string;
  readonly languages: readonly ActivePlatformLanguageView[];
}) {
  const toggle = (code: string) => {
    if (value.includes(code)) onChange(value.filter((x) => x !== code));
    else onChange([...value, code]);
  };
  return (
    <>
      <div className="nw-onboard__step-label">{t('web.onboarding.step3.label')}</div>
      <h1 className="nw-onboard__title">{t('web.onboarding.step3.title')}</h1>
      <p className="nw-onboard__sub">{t('web.onboarding.step3.subtitle')}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {languages.map((l) => {
          const active = value.includes(l.code);
          const isPrimary = l.code === primary;
          return (
            <button
              type="button"
              key={l.code}
              className={`nw-topic-chip ${active || isPrimary ? 'is-on' : ''}`}
              onClick={() => !isPrimary && toggle(l.code)}
              disabled={isPrimary}
              style={{ padding: '8px 14px', fontSize: 13 }}
              aria-pressed={active || isPrimary}
            >
              <span style={{ marginRight: 6 }}>{l.flag}</span>
              {l.name}
            </button>
          );
        })}
      </div>
    </>
  );
}

interface ChannelOption {
  readonly id: string;
  readonly name: string;
  readonly lang: string;
  readonly country: string;
}

function StepChannels({
  t,
  value,
  onChange,
  options,
}: {
  readonly t: TFunction;
  readonly value: ReadonlySet<string>;
  readonly onChange: (v: ReadonlySet<string>) => void;
  readonly options: readonly ChannelOption[];
}) {
  const toggle = (id: string) => {
    const next = new Set(value);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };
  return (
    <>
      <div className="nw-onboard__step-label">{t('web.onboarding.step4.label')}</div>
      <h1 className="nw-onboard__title">{t('web.onboarding.step4.title')}</h1>
      <p className="nw-onboard__sub">{t('web.onboarding.step4.subtitle')}</p>
      <div className="nw-channel-grid">
        {options.map((c) => {
          const active = value.has(c.id);
          return (
            <button
              type="button"
              key={c.id}
              className={`nw-channel-tile ${active ? 'is-on' : ''}`}
              onClick={() => toggle(c.id)}
              aria-pressed={active}
            >
              <ChannelLogo name={c.name} size={40} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="nw-channel-tile__name">{c.name}</div>
                <div className="nw-channel-tile__meta">
                  {c.lang} · {c.country}
                </div>
              </div>
              <span
                className="nw-radio-row__check"
                style={{
                  borderColor: active ? 'var(--accent)' : 'var(--card-border)',
                  background: active ? 'var(--accent)' : 'transparent',
                }}
              >
                {active && <CheckIcon size={12} stroke={3} />}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}
